import React, {
  Children,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { AnimatePresence, motion } from "motion/react";

import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";

const getModalVariants = (prefersReducedMotion) => {
  if (prefersReducedMotion) {
    return {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0 } },
      exit: { opacity: 0, transition: { duration: 0 } },
    };
  }

  return {
    hidden: {
      opacity: 0,
      scale: 0.92,
      y: 24,
    },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      scale: 0.96,
      y: -16,
      transition: {
        duration: 0.25,
        ease: "easeIn",
      },
    },
  };
};

const DialogMotionTransition = forwardRef(function DialogMotionTransition(
  props,
  ref
) {
  const { in: inProp, children, onEnter, onExited, ...other } = props;
  const prefersReducedMotion = usePrefersReducedMotion();
  const nodeRef = useRef(null);

  const setCombinedRef = useCallback(
    (instance) => {
      nodeRef.current = instance;
      if (typeof ref === "function") {
        ref(instance);
      } else if (ref) {
        // eslint-disable-next-line no-param-reassign
        ref.current = instance;
      }
    },
    [ref]
  );

  useEffect(() => {
    const node = nodeRef.current;
    if (inProp && onEnter && node) {
      onEnter(node, true);
    }
  }, [inProp, onEnter]);

  const child = Children.only(children);
  const MotionChild = useMemo(() => motion.create(child.type), [child.type]);

  const {
    style: childStyle,
    className: childClassName,
    ...childRestProps
  } = child.props || {};

  const { style: otherStyle, className: otherClassName, ...restProps } = other;

  return (
    <AnimatePresence
      mode="wait"
      onExitComplete={() => {
        const node = nodeRef.current || null;
        if (onExited) {
          onExited(node);
        }
      }}
    >
      {inProp ? (
        <MotionChild
          key="dialog-motion"
          ref={setCombinedRef}
          variants={getModalVariants(prefersReducedMotion)}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{ ...childStyle, ...otherStyle }}
          className={[childClassName, otherClassName].filter(Boolean).join(" ")}
          {...childRestProps}
          {...restProps}
        />
      ) : null}
    </AnimatePresence>
  );
});

export default DialogMotionTransition;
